import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import { FilterExpression, Pageable } from '@infuse/api/common';
import { last } from 'lodash';
import { searchTenants, Tenant } from '@infuse/api/iam/tenant';

const createSearchFilter = (searchQuery: string): FilterExpression | undefined => {
  if (searchQuery === '') {
    return;
  }
  const filterOptions = searchQuery
    .toLowerCase()
    .split(' ')
    .filter((part: string): boolean => part !== undefined && part !== null && part !== '')
    .map((part: string): string => `%${part}%`)
    .map((part: string): FilterExpression => {
      return new FilterExpression().like('lower(name)', part);
    });
  if (filterOptions.length === 0) {
    return;
  }
  return filterOptions.reduce((acc, cv) => acc.and(cv));
};

export const useTenants = () => {
  const searchQuery = ref<string | undefined>();
  const pageSize = 20;
  const query = useInfiniteQuery<Pageable<Tenant>>({
    initialPageParam: 0,
    queryKey: ['tenants', searchQuery],
    queryFn: async ({ pageParam }) => {
      const apiResponse = await searchTenants({
        size: pageSize,
        page: Number.parseInt(`${pageParam}`),
        filter: searchQuery.value ? createSearchFilter(searchQuery.value) : undefined,
        sort: [{ key: 'name', descending: false }]
      });
      return apiResponse;
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.pageable.pageNumber + 1)
  });
  const tenants = computed(() => query.data.value?.pages.flatMap((page) => page.content) || []);
  const currentPage = computed(() => (query.data.value ? (last(query.data.value?.pageParams) as number) : 0));
  return {
    ...query,
    searchQuery,
    currentPage,
    tenants
  };
};
